import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchAccount, fetchHistory, fetchPositions, fetchOrders, PortfolioHistoryParams } from '../reducers/positionSlice';
import { LandingPage } from '../routes/landing-page';
import { timeFrameConfig } from '../utils/constants';
import { fetchBars, SymbolBarsParams } from '../reducers/symbolSlice';
import { getDateString, getStartDate } from '../utils/utils';

export const LandingPageContainer: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    // Access state from the Redux store
    const { positions, orders, error, account, history } = useSelector((state: RootState) => state.alpaca);
    const { symbols } = useSelector((state: RootState) => state.symbols);
    const { marketDates } = useSelector((state: RootState) => state.lookups);
    const hasInitialized = React.useRef(false);
    
    let date = new Date(marketDates[marketDates.length - 1].date);
    if (date.getUTCDay() !== new Date().getDay()) {
        date = new Date(marketDates[marketDates.length - 2].date);
    }
    const [ chartConfig, setChartConfig ] = React.useState<PortfolioHistoryParams>({
        period: '1D',
        timeframe: '5Min',
        intraday_reporting: 'extended_hours',
        pnl_reset: 'per_day',
        startDate: `${getDateString(date)}T06:30:00Z`,
        endDate: `${getDateString(date)}T17:00:00Z`
    });

    const [ spyConfig, setSpyConfig ] = React.useState<SymbolBarsParams>({
        symbol: 'SPY',
        timeframe: '5Min',
        period: '1D',
        startDate: `${getDateString(date)}T06:30:00Z`,
        endDate: `${getDateString(date)}T17:00:00Z`
    });


    // Fetch users on component mount
    React.useEffect(() => {
        if (!hasInitialized.current) {
            dispatch(fetchPositions());
            dispatch(fetchAccount());
            dispatch(fetchHistory(chartConfig));
            dispatch(fetchBars(spyConfig));
            
            // Only fetch orders once from past year to reduce API load
            // Check if orders are already loaded before fetching
            if (orders.length === 0) {
                const oneYearAgoDate = getStartDate('1Y', new Date());
                dispatch(fetchOrders(oneYearAgoDate));
            }
            
            hasInitialized.current = true;
        }
        
        const timer = setInterval(() => {
            dispatch(fetchPositions());
            dispatch(fetchAccount());
        }, 1 * 60 * 1000);

        return () => {
            clearInterval(timer);
        }
    }, []); // Empty dependency - only initialize once

    const updateBars = (period: string) => {
        let date = new Date(marketDates[marketDates.length - 1].date);
        if (date.getUTCDay() !== new Date().getDay()) {
            date = new Date(marketDates[marketDates.length - 2].date);
        }
        const startDate = getStartDate(period, date);
        const historyParams = {
            ...chartConfig,
            period,
            timeframe: timeFrameConfig[period],
            startDate
        }
        const spyConfigParams = {
            ...spyConfig,
            period,
            timeframe: timeFrameConfig[period],
            startDate
        }

        setChartConfig(historyParams);
        setSpyConfig(spyConfigParams);
        dispatch(fetchHistory(historyParams));
        dispatch(fetchBars(spyConfigParams));
    }

    // Calculate metrics
    const activePositions = positions.length;
    const totalTrades = orders.filter(o => o.side === 'sell').length; // Count of closed trades

    if (!positions.length || !symbols?.SPY) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <LandingPage positions={positions} account={account} history={history} chartPeriod={chartConfig.period} spyChartPeriod={spyConfig.period} spyBars={symbols.SPY.bars} updateBars={updateBars} activePositions={activePositions} totalTrades={totalTrades} orders={orders} />
    );
};
